import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import { AlertColor, Box, Button, Chip, Link, Paper, TextField, Typography } from '@mui/material';
import Notification from 'components/Notification';
import PageLoader from 'components/PageLoader';
import strings from 'common/Translation/Translate';
import dayjs from 'dayjs';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from 'utils/axios';
import Config from 'utils/Config';
import { URLS } from 'utils/constants/urls';
import { BookingDetailSectionTitle } from './BookingDetailSectionTitle';

const MAX_NOTE_FILE_BYTES = 10 * 1024 * 1024;

type NoteEntry = {
  datetime?: string;
  createdAt?: string;
  createdBy?: string;
  summary?: string;
  attachmentUrl?: string;
  fileName?: string;
  fileSize?: number;
};

const formatFileSize = (bytes?: number | null) => {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) {
    const kb = bytes / 1024;
    return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  }
  const mb = bytes / (1024 * 1024);
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
};

const fileLabel = (entry: NoteEntry) => {
  const name = String(entry.fileName || '').trim() || strings.paymentDetails_text_attachment;
  const size = formatFileSize(entry.fileSize);
  return size ? `${name} · ${size}` : name;
};

interface CommentProps {
  bookingLineItemId: number;
  allComments: any[];
  getInitialResponse: () => void;
}

const formatNoteMeta = (entry: NoteEntry) => {
  const at = dayjs(entry.datetime || entry.createdAt);
  const parts: string[] = [];
  if (at.isValid()) {
    parts.push(at.format('D MMM YYYY'));
    parts.push(at.format('hh:mm A'));
  }
  const author = String(entry.createdBy || '').trim() || 'anonymous';
  parts.push(strings.beo_amendment_added_by.replace('{name}', author));
  return parts.join(' . ');
};

const NotesTimeline: FC<{ notes: NoteEntry[] }> = ({ notes }) => {
  if (!notes.length) return null;

  return (
    <Box sx={{ pt: 1, pb: 0.5 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1.5 }}>{strings.booking_logs_notes_timeline}</Typography>
      {notes.map((entry, index) => {
        const isLast = index === notes.length - 1;
        return (
          <Box key={`${entry.createdAt}-${index}`} sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5 }}>
            <Box
              sx={{
                width: 22,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  fontSize: 11,
                  fontWeight: 700,
                  lineHeight: '22px',
                  textAlign: 'center',
                  flexShrink: 0,
                }}>
                {index + 1}
              </Box>
              {!isLast && (
                <Box
                  sx={{
                    width: 2,
                    flex: 1,
                    bgcolor: 'divider',
                    my: 0.5,
                    borderRadius: 1,
                    minHeight: 12,
                  }}
                />
              )}
            </Box>
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                minWidth: 0,
                mb: isLast ? 0.5 : 2,
                px: 1.75,
                py: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                bgcolor: 'background.paper',
              }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: 'text.secondary',
                  lineHeight: 1.4,
                }}>
                {formatNoteMeta(entry)}
              </Typography>
              {entry.summary ? (
                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: 13,
                    lineHeight: 1.5,
                    color: 'text.primary',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}>
                  {entry.summary}
                </Typography>
              ) : null}
              {entry.attachmentUrl ? (
                <Link
                  href={`${Config.BaseURL}${entry.attachmentUrl}`}
                  target="_blank"
                  rel="noopener"
                  underline="hover"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mt: 0.75,
                    px: 1,
                    py: 0.35,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    fontSize: 12,
                    fontWeight: 600,
                    maxWidth: '100%',
                  }}>
                  <AttachFileIcon sx={{ fontSize: 16, transform: 'rotate(45deg)', flexShrink: 0 }} />
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {fileLabel(entry)}
                  </Box>
                </Link>
              ) : null}
            </Paper>
          </Box>
        );
      })}
    </Box>
  );
};

const BookingComments: FC<CommentProps> = ({ bookingLineItemId, allComments, getInitialResponse }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const notes = useMemo<NoteEntry[]>(
    () =>
      [...(allComments ?? [])]
        .map(item => ({
          datetime: item.createdAt,
          createdAt: item.createdAt,
          createdBy: item.createdBy,
          summary: item.comment,
          attachmentUrl: item.fileUrl,
          fileName: item.fileName,
          fileSize: item.fileSize,
        }))
        .sort((a, b) => dayjs(b.datetime || b.createdAt).valueOf() - dayjs(a.datetime || a.createdAt).valueOf()),
    [allComments],
  );

  useEffect(() => {
    setInputValue('');
    setFile(null);
  }, [bookingLineItemId]);

  const handleError = (err: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: err.response?.data?.error_description ?? err.response?.data?.message ?? err.message,
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.files?.[0];
    event.target.value = '';
    if (!next) return;
    if (next.size > MAX_NOTE_FILE_BYTES) {
      setMessage({
        display: true,
        severity: 'error',
        message: strings.booking_logs_file_too_large,
      });
      return;
    }
    setFile(next);
  };

  const handleSave = async () => {
    const note = inputValue.trim();
    if (!note) return;
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('comment', note);
      if (file) formData.append('file', file);

      const { status } = await axiosInstance.post(URLS.bookingLineItemComments(bookingLineItemId), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (status === 200) {
        setMessage({
          display: true,
          severity: 'success',
          message: strings.booking_logs_note_added,
        });
        getInitialResponse();
        setInputValue('');
        setFile(null);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      {isLoading && <PageLoader />}

      <Paper
        elevation={0}
        sx={{
          mb: 2.5,
          px: 1.75,
          py: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          bgcolor: 'background.paper',
        }}>
        <BookingDetailSectionTitle icon={StickyNote2OutlinedIcon} sx={{ mb: 1.25 }}>
          {strings.booking_logs_add_note}
        </BookingDetailSectionTitle>
        <TextField
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          placeholder={strings.booking_logs_note_placeholder}
          multiline
          minRows={4}
          fullWidth
          size="small"
        />
        <input ref={fileInputRef} type="file" hidden onChange={handleFileChange} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            mt: 1.25,
            flexWrap: 'wrap',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AttachFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0 }}>
              {strings.booking_logs_attach_file}
            </Button>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{strings.booking_logs_max_file_size}</Typography>
            {file ? (
              <Chip
                size="small"
                label={file.name}
                onDelete={() => setFile(null)}
                deleteIcon={<CloseIcon />}
                sx={{ maxWidth: 220, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
              />
            ) : null}
          </Box>
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={!inputValue.trim() || isLoading}
            sx={{ textTransform: 'none', fontWeight: 600, ml: 'auto' }}>
            {strings.booking_logs_add_note}
          </Button>
        </Box>
      </Paper>

      <NotesTimeline notes={notes} />
    </>
  );
};

export default BookingComments;
